import { ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountNumber,
  AccountType,
  Country,
  CountryRowDefaultValues,
  DefaultAccWithType,
} from '@/core/types';
import { useAppSelector } from '@/hooks';
import { useCOA } from './Seller/useCOA';
import PageLayout from '@/wrappers/PageLayout';
import { COATabHeader } from '@/components/COATabHeader';
import CountryLabel from '@/components/CountryLabel/CountryLabel';
import { COACell } from '@/components/COACell';
import {
  COUNTRY_ACCS_TYPES,
  isCustom,
  OSS_ACCS_TYPES,
} from '@/components/ChartOfAccounts/ChartOfAccounts.helpers';
import { getChangelogs, postClientAccountNumbers } from '@/core/api';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import useNotification from '@/hooks/useNotification';
import { COACountryRow } from '../components/COACountryRow';
import LabelSwitch from '@/components/LabelSwitch/LabelSwitch';
import { useTranslation } from 'react-i18next';
import { COACountryCollapse } from '../components/COACountryCollapse';
import { COARevenueAccountsInfoBlock } from '@/components/COARevenueAccountsInfoBlock';
import { Loader } from '@/components/Loader/Loader';
import { COALastUpdated } from '@/components/ChartOfAccounts/COALastUpdated';

interface CountryRow {
  vat_rate_high: string;
  vat_rate_middle: string;
  vat_rate_low: string;
  vat_rate_low_second: string;
  b2b_buyer_vat_number_not_null: string;
  arrival_country_not_EU: string;
  commingling_buy_taxable: string;
  commingling_buy_tax_exempt: string;
}

type FormValues = Record<string, string | CountryRow>;

type ViewType = 'list' | 'single';

export const COACountries = () => {
  const queryKey = useAppSelector(queryKeySelector);
  const activeCompany = useAppSelector(activeCompanySelector);
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  const {
    accountTypes,
    defaultAccountNumbers,
    clientAccountNumbers,
    countries,
  } = useCOA();

  const querClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<ViewType>('single');

  const [isEditing, setIsEditing] = useState(false);
  const [accountsMap, setAccountsMap] = useState<Record<string, AccountNumber>>(
    {}
  );
  const [countryAccountsMap, setCountryAccountsMap] = useState<
    Record<string, { [key: string]: AccountNumber }>
  >({});

  const [initialState, setInitialState] = useState<FormValues>(
    {} as FormValues
  );

  const countriesList = useMemo(
    () =>
      countries?.filter(
        (c) =>
          (c.is_eu || c.short_name === 'SM') &&
          c.short_name !== 'XI' &&
          c.short_name !== 'MC' &&
          c.short_name !== 'DE'
      ),
    [countries]
  );
  const countriesAccsIds = clientAccountNumbers
    ?.filter((a) => {
      return (
        ((OSS_ACCS_TYPES.includes(a.account_type.account_type) && !a.country) ||
          (a.country &&
            countriesList?.some(
              (c) => c.short_name === a.country?.short_name
            ) &&
            COUNTRY_ACCS_TYPES.includes(a.account_type.account_type))) &&
        a.account_number
      );
    })
    .map((a) => a.id);

  const {
    data: countriesAccsChangeLogs,
    isLoading: isLoadingCountriesAccsChangeLogs,
  } = useQuery({
    queryKey: ['CountriesAccsChangeLogs', queryKey],
    queryFn: () =>
      getChangelogs(
        activeCompany?.company_id || '',
        'account_number',
        countriesAccsIds
      ),
    enabled: !!activeCompany?.company_id && !!countriesAccsIds?.length,
  });

  const accountTypesMap = useMemo(() => {
    return (accountTypes || []).reduce(
      (acc: Record<string, AccountType>, item: AccountType) => {
        acc[item.account_type] = item;
        return acc;
      },
      {}
    );
  }, [accountTypes]);

  const formik = useFormik<FormValues>({
    initialValues: {
      oss_netto: '',
      oss_vat: '',
      rest: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      const entries = Object.entries(values);
      entries.forEach(([k, v]) => {
        if (typeof v !== 'string') return;
        if (!v) {
          errors[k as keyof FormValues] = 'Required';
        } else if (v && (v.length < 4 || v.length > 7)) {
          errors[k as keyof FormValues] = 'Between 4—7 digits';
        } else if (v && +v <= 0) {
          errors[k as keyof FormValues] = 'Account number invalid';
        }
      });

      entries
        .filter(([, v]) => typeof v === 'object')
        .forEach(([k, v]) => {
          Object.entries(v).forEach(([field, value]) => {
            if (typeof v !== 'object') return;

            if (!value) {
              errors[k as keyof FormValues] = {
                ...((errors as any)[k] || {}),
                [field]: 'Required',
              };
            } else if (value && (value.length < 4 || value.length > 7)) {
              errors[k as keyof FormValues] = {
                ...((errors as any)[k] || {}),
                [field]: 'Between 4—7 digits',
              };
            } else if (value && +value <= 0) {
              errors[k as keyof FormValues] = {
                ...((errors as any)[k] || {}),
                [field]: 'Account number invalid',
              };
            }
          });
        });
      return errors;
    },
    onSubmit: async (values) => {
      if (!activeCompany?.company_id || !defaultAccountNumbers) return;
      const countryAccountNumbers = Object.entries(values)
        .filter(([k]) => {
          return !!countriesMap[k];
        })
        .map(([k, v]) => {
          return Object.entries(v)
            .filter(([, v]) => !!v)
            .map(([type, value]) => {
              const id = countryAccountsMap[k]?.[type]?.id;
              if (
                !id &&
                defaultAccountNumbers.some(
                  (a) =>
                    a.country?.short_name === k &&
                    a.account_number === String(value)
                )
              ) {
                return null;
              }

              return {
                account_type: accountTypesMap[type].id,
                country: countriesMap[k].id,
                account_number: +value,
                id,
              };
            });
        })
        .flat();

      const accountNumbers = Object.entries(values)
        .filter(([k]) => {
          return !countriesMap[k];
        })
        .map(([k, value]) => {
          const id = accountsMap[k]?.id;
          if (
            !id &&
            defaultAccountNumbers.some(
              (a) => a.account_type === k && a.account_number === String(value)
            )
          ) {
            return null;
          }

          return {
            account_type: accountTypesMap[k].id,
            account_number: +value,
            id,
          };
        });

      try {
        const res = await postClientAccountNumbers(
          activeCompany.company_id,
          [...countryAccountNumbers, ...accountNumbers].filter(
            (v): v is NonNullable<typeof v> => v != null
          )
        );

        querClient.setQueryData(['clientAccountNumbers', queryKey], res);

        const changelogsRes = await getChangelogs(
          activeCompany?.company_id || '',
          'account_number',
          countriesAccsIds
        );

        querClient.setQueryData(
          ['CountriesAccsChangeLogs', queryKey],
          changelogsRes
        );

        setIsEditing(false);
        showNotification({
          text: 'Account numbers are updated successfully!',
          type: 'success',
        });
      } catch (error) {
        console.error(error);
      }
    },
  });

  const countriesMap = useMemo(
    () =>
      (countriesList || []).reduce((acc: Record<string, Country>, c) => {
        acc[c.short_name] = c;
        return acc;
      }, {}),
    [countriesList]
  );

  const filteredDefaultAccs = useMemo(() => {
    if (!defaultAccountNumbers || !countriesList) return undefined;
    return defaultAccountNumbers?.filter(
      (a) =>
        (COUNTRY_ACCS_TYPES.includes(a.account_type) &&
          countriesList.some((c) => c.short_name === a.country?.short_name)) ||
        (!a.country?.short_name && OSS_ACCS_TYPES.includes(a.account_type))
    );
  }, [defaultAccountNumbers, countriesList]);

  const normalizedDefaultAccs = useMemo(() => {
    return normalizeAccounts(filteredDefaultAccs);
  }, [filteredDefaultAccs]);

  useEffect(() => {
    if (clientAccountNumbers && defaultAccountNumbers && countriesList) {
      const initialValues = {};

      defaultAccountNumbers
        .filter((a) => {
          return (
            ((OSS_ACCS_TYPES.includes(a.account_type) && !a.country) ||
              (a.country &&
                countriesList.some(
                  (c) => c.short_name === a.country?.short_name
                ) &&
                COUNTRY_ACCS_TYPES.includes(a.account_type))) &&
            a.account_number
          );
        })
        .forEach((a) => {
          if (
            a.country?.short_name &&
            COUNTRY_ACCS_TYPES.includes(a.account_type)
          ) {
            const v = (
              initialValues as unknown as {
                [key: string]: CountryRow;
              }
            )[a.country.short_name];

            (
              initialValues as unknown as {
                [key: string]: CountryRow;
              }
            )[a.country.short_name] = {
              ...v,
              [a.account_type]: String(a.account_number),
            };
          }

          if (a.account_type in formik.values && !a.country) {
            (initialValues as unknown as Record<string, string>)[
              a.account_type
            ] = String(a.account_number);
          }
        });

      const countryAccs: Record<string, Record<string, AccountNumber>> = {};
      const accs: Record<string, AccountNumber> = {};

      clientAccountNumbers
        .filter((a) => {
          return (
            ((OSS_ACCS_TYPES.includes(a.account_type.account_type) &&
              !a.country) ||
              (a.country &&
                countriesList.some(
                  (c) => c.short_name === a.country?.short_name
                ) &&
                COUNTRY_ACCS_TYPES.includes(a.account_type.account_type))) &&
            a.account_number
          );
        })
        .forEach((a) => {
          if (a.country) {
            const countryKey = a.country.short_name;
            countryAccs[countryKey] = {
              ...countryAccs[countryKey],
              [a.account_type.account_type]: a,
            };

            (
              initialValues as unknown as {
                [key: string]: CountryRow;
              }
            )[countryKey][a.account_type.account_type as keyof CountryRow] =
              String(a.account_number || '');
          } else {
            accs[a.account_type.account_type] = a;
            (initialValues as unknown as Record<string, string>)[
              a.account_type.account_type
            ] = String(a.account_number);
          }
        });

      setCountryAccountsMap(countryAccs);
      setAccountsMap(accs);
      formik.setValues(initialValues);
      setInitialState(initialValues);
    }
    // eslint-disable-next-line
  }, [clientAccountNumbers, defaultAccountNumbers, countriesList]);

  const getCountryFieldProps = (code: string, field: keyof CountryRow) => {
    return {
      value: (formik.values as unknown as Record<string, CountryRow>)[code]?.[
        field
      ],
      error:
        (formik.errors as unknown as Record<string, Record<string, string>>)[
          code
        ]?.[field] || '',
      touched:
        (formik.touched as unknown as Record<string, Record<string, boolean>>)[
          code
        ]?.[field] || false,
    };
  };

  const onChangeCountryField: (
    code: string,
    field: string
  ) => ChangeEventHandler<HTMLInputElement> = (code, field) => (e) => {
    formik.setFieldValue(code + '.' + field, String(e.target.value), true);
  };

  const onBlurCountryField = (field: string) => () => {
    formik.setFieldTouched(field, true);
  };

  if (isLoadingCountriesAccsChangeLogs) return <Loader />;

  return (
    <PageLayout
      className='flex-grow !bg-white !pt-0'
      rightColumn={
        <div className='max-w-[248px]'>
          <COARevenueAccountsInfoBlock />
        </div>
      }
    >
      <div>
        <COATabHeader
          className='!items-start'
          title='Revenue Accounts Countries'
          onClickCancel={() => {
            formik.setValues(initialState);
            setIsEditing(false);
          }}
          onClickUpdate={() => formik.submitForm()}
          onClickEdit={() => setIsEditing(true)}
          isLoading={formik.isSubmitting}
          isEditing={isEditing}
          hasCustomAccountNumbers={
            !!(clientAccountNumbers || []).filter((a) => {
              const countryAccs =
                a.country &&
                normalizedDefaultAccs.normalizedCountryAccsMap[
                  a.country.short_name
                ];

              return (
                (!a.country &&
                  a.account_type.account_type in
                    normalizedDefaultAccs.normalizedAccsMap) ||
                (countryAccs && a.account_type.account_type in countryAccs)
              );
            }).length
          }
        >
          {!!countriesAccsChangeLogs?.length && (
            <div className='text-sm text-gray-700'>
              {t('Last updated')}:{' '}
              <COALastUpdated
                date={countriesAccsChangeLogs[0].created_at}
                role={countriesAccsChangeLogs[0].role_name}
              />
            </div>
          )}
        </COATabHeader>
        <div className='mb-2 rounded-lg border p-4 pt-2'>
          <CountryLabel country='EU' className='font-semibold' />
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>{t('oss_netto_label')}</div>
            <div>
              <COACell
                value={formik.values.oss_netto as string}
                isEditing={isEditing}
                error={formik.errors.oss_netto || ''}
                touched={formik.touched.oss_netto || false}
                testId='oss_netto'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='oss_netto'
                isCustom={isCustom(
                  formik.values.oss_netto as string,
                  normalizedDefaultAccs.normalizedAccsMap.oss_netto
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>{t('oss_vat_label')}</div>
            <div>
              <COACell
                value={formik.values.oss_vat as string}
                isEditing={isEditing}
                error={formik.errors.oss_vat || ''}
                touched={formik.touched.oss_vat || false}
                testId='oss_vat'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='oss_vat'
                isCustom={isCustom(
                  formik.values.oss_vat as string,
                  normalizedDefaultAccs.normalizedAccsMap.oss_vat
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
        </div>
        <div className='mb-2 flex items-center justify-between gap-x-2 rounded-lg border px-4 pt-4'>
          <div className='pb-4 text-h4 font-bold'>{t('Standard Account')}</div>
          <div>
            <COACell
              value={formik.values.rest as string}
              isEditing={isEditing}
              error={formik.errors.rest || ''}
              touched={formik.touched.rest || false}
              testId='rest'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name='rest'
              isCustom={isCustom(
                formik.values.rest as string,
                normalizedDefaultAccs.normalizedAccsMap.rest
              )}
              inputWrapperClassName={`min-h-[66px] flex items-center`}
            />
          </div>
        </div>
        <div className='mb-2 rounded-lg border '>
          <div className='flex items-center justify-between border-b px-4 py-2'>
            <div className='text-h4 font-bold'>
              {t('Revenue Accounts Countries')} ({countriesList?.length})
            </div>
            <LabelSwitch<ViewType>
              activeItem={selectedType}
              onClick={(v) => () => {
                setSelectedType(v);
              }}
              items={[
                { value: 'single', label: t('Single') },
                { value: 'list', label: t('List') },
              ]}
            />
          </div>
          <div className='divide-y'>
            {(countriesList || [])
              .sort((a, b) => t(a.short_name).localeCompare(t(b.short_name)))
              .map((c, i) => {
                const vatRateHigh = getCountryFieldProps(
                  c.short_name,
                  'vat_rate_high'
                );
                const vatRateMiddle = getCountryFieldProps(
                  c.short_name,
                  'vat_rate_middle'
                );
                const vatRateLow = getCountryFieldProps(
                  c.short_name,
                  'vat_rate_low'
                );
                const b2bBuyerVatNumberNotNull = getCountryFieldProps(
                  c.short_name,
                  'b2b_buyer_vat_number_not_null'
                );
                const arrivalCountryNotEU = getCountryFieldProps(
                  c.short_name,
                  'arrival_country_not_EU'
                );
                const comminglingBuyTaxable = getCountryFieldProps(
                  c.short_name,
                  'commingling_buy_taxable'
                );
                const comminglingBuyTaxExempt = getCountryFieldProps(
                  c.short_name,
                  'commingling_buy_tax_exempt'
                );

                const props = {
                  onBlurCountryField,
                  onChangeCountryField,
                  vatRateHigh,
                  vatRateMiddle,
                  vatRateLow,
                  b2bBuyerVatNumberNotNull,
                  arrivalCountryNotEU,
                  country: c,
                  isEditing,
                  key: c.short_name,
                  defaultNumbers:
                    normalizedDefaultAccs.normalizedCountryAccsMap[
                      c.short_name
                    ],
                  comminglingBuyTaxable,
                  comminglingBuyTaxExempt,
                };

                if (selectedType === 'single') {
                  return (
                    <COACountryCollapse {...props} initialOpened={i === 0}>
                      {c.short_name === 'PT' ? (
                        <div className='whitespace-pre-line pt-2 text-sm'>
                          {
                            'Die autonomen Regionen Madeira und Azoren haben unterschiedliche Sätze.\nSie werden jedoch in denselben Konten erfasst.'
                          }
                        </div>
                      ) : null}
                    </COACountryCollapse>
                  );
                }
                return <COACountryRow {...props} />;
              })}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const normalizeAccounts = (accounts: DefaultAccWithType[] | undefined) => {
  if (!accounts)
    return {
      normalizedAccsMap: {},
      normalizedCountryAccsMap: {},
    };
  const normalizedCountryAccsMap = accounts
    .filter((a) => COUNTRY_ACCS_TYPES.includes(a.account_type))
    .reduce(
      (acc, a) => {
        const countryKey = a.country?.short_name;
        if (!countryKey) return acc;

        acc[countryKey] = {
          ...(acc[countryKey] || {}),
          [a.account_type]: String(a.account_number),
        };

        return acc;
      },
      {} as Record<string, CountryRowDefaultValues>
    );

  const normalizedAccsMap = accounts
    .filter((a) => OSS_ACCS_TYPES.includes(a.account_type))
    .reduce(
      (acc, a) => {
        if (a.country) return acc;
        acc[a.account_type] = String(a.account_number);
        return acc;
      },
      {} as Record<string, string>
    );

  return {
    normalizedAccsMap,
    normalizedCountryAccsMap,
  };
};
